/**
 * Credit Settlement – Corporate Individual Payment.
 * GET /histopathology/billing/corporate/bills/?corporate_id&bill_type&from_date&to_date
 *     &patient_name&bill_number
 * POST /histopathology/credit-settlement/corporate/collect/  for Collect All
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Paper, Typography, TextField, IconButton, Tooltip, Button, Grid,
  MenuItem, FormControl, InputLabel, Select, Dialog, DialogContent, DialogActions,
  Divider, Checkbox, RadioGroup, Radio, FormControlLabel, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { DateTime } from "luxon";
import { useLocation, useNavigate } from "react-router-dom";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { canEditTab } from "../../Utility/Services/RoleAccessService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import CommonValidation from "../../Utility/CommonValidation";
import { CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import CommonDatePicker from "../../Components/Common Components/CommonDatePicker";
import { CurrencySymbol } from "../../Utility/Constants";
import "../../Styles/Configuration.css";

const InfoCell = ({ label, value }) => {
  const display = value || "-";
  return (
    <Box sx={{ minWidth: 0, width: "100%", overflow: "hidden" }}>
      <Typography sx={{ fontSize: "0.8vw", color: "#666" }}>{label}</Typography>
      <Tooltip title={display} placement="top" arrow>
        <Typography
          sx={{
            fontSize: "1vw",
            fontWeight: 600,
            color: "#1a1a1a",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {display}
        </Typography>
      </Tooltip>
    </Box>
  );
};

export default function CorporateIndividualPayment() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const provider = state || {};
  const canEdit = canEditTab("Hospital Settlement");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const todayStr = DateTime.now().toFormat("yyyy-MM-dd");
  const [fromDate, setFromDate] = useState(provider.from_date || todayStr);
  const [toDate, setToDate] = useState(provider.to_date || todayStr);
  const [dateIndex, setDateIndex] = useState(provider.from_date ? 5 : 1);
  const [search, setSearch] = useState("");
  const [billType, setBillType] = useState(provider.bill_type || "outstanding");
  const [selection, setSelection] = useState({ type: "include", ids: new Set() });
  const selectionCount = selection?.ids?.size ?? 0;
  const [providerInfo, setProviderInfo] = useState({
    company_name: provider.corporate_name,
    mobile: provider.mobile,
    email: provider.email,
  });
  const [providerTotals, setProviderTotals] = useState({
    bills_count: provider.bills_count ?? 0,
    payable_amount: +provider.payable_amount || 0,
    received_amount: +provider.received_amount || 0,
    outstanding_amount: +provider.outstanding_amount || 0,
  });
  const [msg, setMsg] = useState({ err: false, errText: "", ok: false, okText: "" });

  const [payOpen, setPayOpen] = useState(false);
  const [paySaving, setPaySaving] = useState(false);
  const [popupSelection, setPopupSelection] = useState(new Set());
  const [payForm, setPayForm] = useState({
    receivable: "",
    tds: "",
    mode: "cash",
    cash_amt: "", card_amt: "", upi_amt: "", bank_amt: "", cheque_amt: "",
    card_no: "", upi_no: "", bank_no: "", cheque_no: "",
    transaction_id: "",
    transaction_date: DateTime.now().toFormat("yyyy-MM-dd"),
    payers_bank_name: "",
    payers_account_no: "",
    receiver_bank_name: "",
    receiver_account_no: "",
    cheque_date: "",
    comments: "",
  });

  const fetchData = useCallback(() => {
    if (!provider.corporate_id) return;
    setLoading(true);
    const params = new URLSearchParams();
    params.append("corporate_id", String(provider.corporate_id));
    if (billType) params.append("bill_type", billType);
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);
    if (search) params.append("patient_name", search);
    const url = `${Serviceurls.HISTOPATHOLOGY_CREDIT_SETTLEMENT_CORPORATE_DETAIL}?${params.toString()}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setRows(res.data.data ?? []);
          if (res.data.corporate) {
            setProviderInfo((prev) => ({ ...prev, ...res.data.corporate }));
          }
          const t = res.data.totals || {};
          setProviderTotals({
            bills_count: +t.bills_count || 0,
            payable_amount: +t.payable_amount || 0,
            received_amount: +t.received_amount || 0,
            outstanding_amount: +t.outstanding_amount || 0,
          });
        } else {
          setRows([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        setMsg({ err: true, errText: err?.response?.data?.message || err?.message, ok: false, okText: "" });
      });
  }, [provider.corporate_id, billType, fromDate, toDate, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const outstandingBills = useMemo(
    () => rows.filter((r) => +r.outstanding_amount > 0),
    [rows]
  );

  const openPaymentPopup = (preselect) => {
    let initial;
    if (preselect instanceof Set) initial = new Set(preselect);
    else if (Array.isArray(preselect)) initial = new Set(preselect);
    else if (preselect != null) initial = new Set([preselect]);
    else initial = new Set();
    setPopupSelection(initial);
    setPayForm((p) => ({
      ...p,
      receivable: "",
      tds: "",
      mode: "cash",
      cash_amt: "", card_amt: "", upi_amt: "", bank_amt: "", cheque_amt: "",
      card_no: "", upi_no: "", bank_no: "", cheque_no: "",
      transaction_id: "",
      payers_bank_name: "",
      payers_account_no: "",
      receiver_bank_name: "",
      receiver_account_no: "",
      cheque_date: "",
      comments: "",
    }));
    setPayOpen(true);
  };

  const closePaymentPopup = () => {
    if (paySaving) return;
    setPayOpen(false);
  };

  const popupTotals = useMemo(() => {
    const sel = outstandingBills.filter((b) => popupSelection.has(b.bill_id ?? b.id));
    const payable = sel.reduce((s, b) => s + (+b.payable_amount || 0), 0);
    const paid = sel.reduce((s, b) => s + (+b.received_amount || 0), 0);
    const outstanding = sel.reduce((s, b) => s + (+b.outstanding_amount || 0), 0);
    return { count: sel.length, payable, paid, outstanding, list: sel };
  }, [outstandingBills, popupSelection]);

  const togglePopupRow = (id) => {
    setPopupSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePopupAll = () => {
    if (popupSelection.size === outstandingBills.length) setPopupSelection(new Set());
    else setPopupSelection(new Set(outstandingBills.map((b) => b.bill_id ?? b.id)));
  };

  const popupAllocations = useMemo(() => {
    const receivable = +payForm.receivable || 0;
    const tds = +payForm.tds || 0;
    const total = receivable + tds;
    const map = {};
    if (total <= 0) return map;
    let remaining = total;
    popupTotals.list.forEach((b) => {
      const due = +b.outstanding_amount || 0;
      const take = Math.min(due, remaining);
      remaining = +(remaining - take).toFixed(2);
      const recvShare = total > 0 ? +((take * receivable) / total).toFixed(2) : 0;
      const tdsShare = +(take - recvShare).toFixed(2);
      map[b.bill_id ?? b.id] = { deducted: take, receivable: recvShare, tds: tdsShare };
    });
    return map;
  }, [popupTotals.list, payForm.receivable, payForm.tds]);

  const handleSettleSubmit = () => {
    if (!popupSelection.size) {
      setMsg({ err: true, errText: "Select at least one bill.", ok: false, okText: "" });
      return;
    }
    const receivable = +payForm.receivable || 0;
    const tds = +payForm.tds || 0;
    const total = receivable + tds;
    if (receivable <= 0) {
      setMsg({ err: true, errText: "Enter Receivable Amount.", ok: false, okText: "" });
      return;
    }
    if (total > popupTotals.outstanding) {
      setMsg({ err: true, errText: "Receivable + TDS exceeds total outstanding.", ok: false, okText: "" });
      return;
    }
    const refByMode = {
      card: payForm.card_no, upi: payForm.upi_no, bank: payForm.bank_no, cheque: payForm.cheque_no,
    };
    if (CommonValidation.isTransactionNumberMandatory()
      && payForm.mode !== "cash"
      && !String(refByMode[payForm.mode] || "").trim()) {
      setMsg({ err: true, errText: "Please enter Reference Number.", ok: false, okText: "" });
      return;
    }
    if (!String(payForm.comments || "").trim()) {
      setMsg({ err: true, errText: "Comments are mandatory.", ok: false, okText: "" });
      return;
    }
    const billsPayload = popupTotals.list
      .map((b) => {
        const a = popupAllocations[b.bill_id ?? b.id] || { receivable: 0, tds: 0, deducted: 0 };
        if (a.deducted <= 0) return null;
        const amt = CommonValidation.formatAmount(a.receivable);
        const zero = 0;
        return {
          bill_id: b.bill_id ?? b.id,
          received_amount: amt,
          tds_amount: CommonValidation.formatAmount(a.tds),
          deducted_amount: CommonValidation.formatAmount(a.deducted),
          cash_amount: payForm.mode === "cash" ? amt : zero,
          card_amount: payForm.mode === "card" ? amt : zero,
          upi_amount: payForm.mode === "upi" ? amt : zero,
          bank_amount: payForm.mode === "bank" ? amt : zero,
          cheque_amount: payForm.mode === "cheque" ? amt : zero,
        };
      })
      .filter(Boolean);
    if (!billsPayload.length) {
      setMsg({ err: true, errText: "Distribution failed.", ok: false, okText: "" });
      return;
    }
    const totalStr = CommonValidation.formatAmount(receivable);
    const zero = 0;
    const payload = {
      corporate_id: provider.corporate_id,
      company_name: providerInfo.company_name || provider.corporate_name,
      bills: billsPayload,
      receipt: {
        received_amount: totalStr,
        cash_amount: payForm.mode === "cash" ? totalStr : zero,
        card_amount: payForm.mode === "card" ? totalStr : zero,
        upi_amount: payForm.mode === "upi" ? totalStr : zero,
        bank_amount: payForm.mode === "bank" ? totalStr : zero,
        cheque_amount: payForm.mode === "cheque" ? totalStr : zero,
        card_ref_no: payForm.mode === "card" ? String(payForm.card_no || "").trim() : "",
        upi_ref_no: payForm.mode === "upi" ? String(payForm.upi_no || "").trim() : "",
        bank_ref_no: payForm.mode === "bank" ? String(payForm.bank_no || "").trim() : "",
        cheque_ref_no: payForm.mode === "cheque" ? String(payForm.cheque_no || "").trim() : "",
        transaction_id: String(payForm.transaction_id || "").trim(),
        transaction_date: payForm.transaction_date || null,
        payers_bank_name: String(payForm.payers_bank_name || "").trim(),
        payers_account_no: String(payForm.payers_account_no || "").trim(),
        receiver_bank_name: String(payForm.receiver_bank_name || "").trim(),
        receiver_account_no: String(payForm.receiver_account_no || "").trim(),
        cheque_no: payForm.mode === "cheque" ? String(payForm.cheque_no || "").trim() : "",
        cheque_date: payForm.mode === "cheque" && payForm.cheque_date ? payForm.cheque_date : null,
      },
      tds_amount: payForm.tds ? CommonValidation.formatAmount(+payForm.tds || 0) : 0,
      comments: String(payForm.comments || "").trim(),
    };
    setPaySaving(true);
    RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_CREDIT_SETTLEMENT_CORPORATE_COLLECT)
      .then((res) => {
        setPaySaving(false);
        if (res?.data?.status === "success") {
          setMsg({ err: false, errText: "", ok: true, okText: res.data.message || "Settlement recorded." });
          setPayOpen(false);
          setSelection({ type: "include", ids: new Set() });
          fetchData();
        } else {
          setMsg({ err: true, errText: res?.data?.message || "Failed to settle.", ok: false, okText: "" });
        }
      })
      .catch((err) => {
        setPaySaving(false);
        setMsg({ err: true, errText: err?.response?.data?.message || err?.message, ok: false, okText: "" });
      });
  };

  const patientName = (pat = {}) => {
    const parts = [pat.title, pat.first_name, pat.surname].filter(Boolean);
    return parts.join(" ").trim();
  };

  const formatBillDate = (s) => {
    if (!s) return "-";
    const str = String(s).trim();
    if (/AM$|PM$/i.test(str)) return str;
    const m = str.match(/^(\d{2}-\d{2}-\d{4})(?:[ T](\d{2}):(\d{2})(?::\d{2})?)?/);
    if (m) {
      const [, datePart, hh, mm] = m;
      if (hh == null) return datePart;
      let h = parseInt(hh, 10);
      const period = h >= 12 ? "PM" : "AM";
      h = h % 12 || 12;
      return `${datePart} ${String(h).padStart(2, "0")}:${mm} ${period}`;
    }
    const dt = DateTime.fromISO(str);
    if (dt.isValid) return dt.toFormat("dd-MM-yyyy hh:mm a");
    return str;
  };

  const columns = [
    { field: "sno", headerName: "S.No", flex: 0.4, minWidth: 60, sortable: false },
    {
      field: "patient_details", headerName: "Patient Details", flex: 1.5, minWidth: 200, sortable: false,
      renderCell: (p) => {
        const pat = p.row.patient || {};
        return (
          <Box sx={{ lineHeight: 1.3 }}>
            <Typography variant="body2" fontWeight={600} sx={{ textTransform: "uppercase" }}>{patientName(pat) || "-"}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[p.row.nxt_id, pat.mobile_no].filter(Boolean).join(" | ") || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "op_ip_number", headerName: "OP / IP Number", flex: 1.2, minWidth: 160, sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2">{p.row?.patient?.op_ip_number || "-"}</Typography>
        </Box>
      ),
    },
    {
      field: "bill_details", headerName: "Bill Details", flex: 1.2, minWidth: 160, sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2" fontWeight={600}>{p.row.bill_number || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">{formatBillDate(p.row.invoice_date)}</Typography>
        </Box>
      ),
    },
    {
      field: "payable_amount", headerName: `Bill Amount (${CurrencySymbol})`, flex: 1, minWidth: 120,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.payable_amount ?? 0),
      renderCell: (p) => CommonValidation.AmountFormat(p.row.payable_amount ?? 0, false),
    },
    {
      field: "received_amount", headerName: `Received Amount (${CurrencySymbol})`, flex: 1.1, minWidth: 140,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.received_amount ?? 0),
      renderCell: (p) => CommonValidation.AmountFormat(p.row.received_amount ?? 0, false),
    },
    {
      field: "outstanding_amount", headerName: `Outstanding Amount (${CurrencySymbol})`, flex: 1.1, minWidth: 150,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.outstanding_amount ?? 0),
      renderCell: (p) => (
        <Typography sx={{ fontSize: "0.85vw", color: +p.row.outstanding_amount > 0 ? "#d32f2f" : "#2e7d32", fontWeight: 600 }}>
          {CommonValidation.AmountFormat(p.row.outstanding_amount ?? 0, false)}
        </Typography>
      ),
    },
    {
      field: "status", headerName: "Status", flex: 0.8, minWidth: 100,
      renderCell: (p) => (
        <Typography sx={{
          fontSize: "0.85vw", textTransform: "capitalize",
          color: p.row.status === "paid" || p.row.status === "Completed" ? "#2e7d32" : "#d32f2f",
          fontWeight: 500,
        }}>
          {p.row.status || "-"}
        </Typography>
      ),
    },
    {
      field: "action", headerName: "Action", sortable: false, flex: 0.5, minWidth: 80,
      align: "center", headerAlign: "center",
      renderCell: (p) => {
        const isFullyPaid = p.row.status === "paid" || p.row.status === "Completed" || +p.row.outstanding_amount <= 0;
        const disabled = !canEdit || isFullyPaid;
        return (
          <Tooltip title={!canEdit ? "No edit access" : (isFullyPaid ? "Settled" : "Settle")}>
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                color={disabled ? "default" : "primary"}
                onClick={() => openPaymentPopup(p.row.bill_id ?? p.row.id)}
              >
                <AccountBalanceWalletOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: "0.8vw", height: "100%", display: "flex", flexDirection: "column", gap: "0.8vw", overflow: "hidden", minHeight: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button startIcon={<ArrowBackIcon />} variant="outlined" size="small"
          onClick={() => navigate("/HistopathologyCreditSettlement/CorporateProvider")}
          sx={{ textTransform: "capitalize" }}>
          Back To Provider
        </Button>
        <Tooltip title={!canEdit ? "No edit access" : ""}>
          <span>
            <Button variant="contained" size="small" disableElevation
              disabled={!canEdit || !selectionCount}
              onClick={() => openPaymentPopup(selection?.ids)}
              sx={{ textTransform: "capitalize" }}>
              {`Collect All (${selectionCount})`}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Paper elevation={1} sx={{ p: "0.8vw", borderRadius: "8px" }}>
        <Typography sx={{ fontSize: "0.8vw", color: "#888", letterSpacing: "0.05em", mb: 0.8 }}>
          PROVIDER INFORMATION
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}><InfoCell label="Hospital" value={providerInfo.company_name || provider.corporate_name} /></Grid>
          <Grid item xs={2}><InfoCell label="No Of Bills" value={providerTotals.bills_count} /></Grid>
          <Grid item xs={2}><InfoCell label="Bill Amount" value={CommonValidation.AmountFormat(providerTotals.payable_amount)} /></Grid>
          <Grid item xs={2}><InfoCell label="Received Amount" value={CommonValidation.AmountFormat(providerTotals.received_amount)} /></Grid>
          <Grid item xs={3}><InfoCell label="OutStanding Amount" value={CommonValidation.AmountFormat(providerTotals.outstanding_amount)} /></Grid>
        </Grid>
      </Paper>

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Box sx={{ flexGrow: 1 }} />
        <FormControl size="small" sx={{ minWidth: "10vw" }}>
          <InputLabel>Bill Type</InputLabel>
          <Select label="Bill Type" value={billType} onChange={(e) => setBillType(e.target.value)}>
            <MenuItem value="outstanding">Outstanding</MenuItem>
            <MenuItem value="history">History</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ minWidth: "12vw" }}>
          <CommonDatePicker
            FromDate={fromDate}
            ToDate={toDate}
            DateIndex={dateIndex}
            removeMarginRight
            SelectedDates={(from, to, idx) => {
              setFromDate(from);
              setToDate(to);
              setDateIndex(idx);
            }}
          />
        </Box>
        <TextField size="small" placeholder="Search" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: "14vw" }} />
      </Stack>

      <Paper sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}>
        <DataGrid
          rows={rows.map((r, i) => ({ ...r, id: r.bill_id ?? r.id ?? i, sno: i + 1 }))}
          columns={columns}
          loading={loading}
          checkboxSelection={canEdit}
          rowSelectionModel={selection}
          onRowSelectionModelChange={(model) => setSelection(model)}
          isRowSelectable={(p) =>
            canEdit && p.row.status !== "paid" && p.row.status !== "Completed" && +p.row.outstanding_amount > 0
          }
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
          pageSizeOptions={[10, 20, 30]}
          rowHeight={56}
          columnHeaderHeight={42}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => <Stack className="eMed_conf_nodata">No Records Found</Stack>,
          }}
          sx={{ border: 0, flex: 1, minWidth: 0 }}
        />
      </Paper>
      <Dialog open={payOpen} onClose={closePaymentPopup} fullWidth maxWidth="lg">
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1.2, borderBottom: "1px solid #e0e0e0" }}>
          <Typography fontWeight={600} fontSize="1vw">
            Payment - {providerInfo.company_name || provider.corporate_name || "-"}
          </Typography>
          <IconButton size="small" onClick={closePaymentPopup} disabled={paySaving}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <DialogContent sx={{ p: 1.5 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={7.5}>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "55vh" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox size="small"
                          checked={outstandingBills.length > 0 && popupSelection.size === outstandingBills.length}
                          indeterminate={popupSelection.size > 0 && popupSelection.size < outstandingBills.length}
                          onChange={togglePopupAll} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Bill Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Bill No</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{`Bill (${CurrencySymbol})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{`Paid (${CurrencySymbol})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{`Outstanding (${CurrencySymbol})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{`Deducted (${CurrencySymbol})`}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {outstandingBills.length === 0 ? (
                      <TableRow><TableCell colSpan={7} align="center"><Typography variant="caption" color="text.secondary">No outstanding bills</Typography></TableCell></TableRow>
                    ) : outstandingBills.map((b) => {
                      const id = b.bill_id ?? b.id;
                      const checked = popupSelection.has(id);
                      const deducted = checked ? (popupAllocations[id]?.deducted ?? 0) : 0;
                      return (
                        <TableRow key={id} hover selected={checked}>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" checked={checked} onChange={() => togglePopupRow(id)} />
                          </TableCell>
                          <TableCell>{formatBillDate(b.invoice_date)}</TableCell>
                          <TableCell>{b.bill_number || "-"}</TableCell>
                          <TableCell align="right">{CommonValidation.AmountFormat(b.payable_amount ?? 0, false)}</TableCell>
                          <TableCell align="right">{CommonValidation.AmountFormat(b.received_amount ?? 0, false)}</TableCell>
                          <TableCell align="right" sx={{ color: "#d32f2f", fontWeight: 600 }}>
                            {CommonValidation.AmountFormat(b.outstanding_amount ?? 0, false)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: deducted > 0 ? "#0288d1" : "#999", fontWeight: deducted > 0 ? 600 : 400 }}>
                            {CommonValidation.AmountFormat(deducted, false)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} md={4.5}>
              <Paper variant="outlined" sx={{ p: 1.5, height: "100%", maxHeight: "55vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.2 }}>
                <Typography fontWeight={600} fontSize="0.9vw">Billing Information</Typography>
                <Divider />
                <Box display="flex" justifyContent="space-between"><Typography variant="body2">Total Payable Amount</Typography>
                  <Typography variant="body2" fontWeight={700}>{CommonValidation.AmountFormat(popupTotals.outstanding)}</Typography>
                </Box>
                <TextField
                  size="small" fullWidth
                  label="Receivable Amount *"
                  value={payForm.receivable}
                  placeholder="0.00"
                  inputProps={{ inputMode: "decimal", maxLength: 12 }}
                  error={!!payForm.receivable && (
                    !CommonValidation.DecimalNumber(payForm.receivable) ||
                    (+payForm.receivable || 0) + (+payForm.tds || 0) > popupTotals.outstanding
                  )}
                  helperText={
                    payForm.receivable && !CommonValidation.DecimalNumber(payForm.receivable)
                      ? "Enter a valid amount"
                      : (payForm.receivable && (+payForm.receivable || 0) + (+payForm.tds || 0) > popupTotals.outstanding
                        ? "Receivable + TDS exceeds outstanding"
                        : "")
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "" || CommonValidation.DecimalNumber(v)) {
                      setPayForm((p) => ({ ...p, receivable: v }));
                    }
                  }}
                />
                <TextField
                  size="small" fullWidth
                  label="TDS Amount"
                  value={payForm.tds}
                  placeholder="0.00"
                  inputProps={{ inputMode: "decimal", maxLength: 12 }}
                  error={!!payForm.tds && (
                    !CommonValidation.DecimalNumber(payForm.tds) ||
                    (+payForm.receivable || 0) + (+payForm.tds || 0) > popupTotals.outstanding
                  )}
                  helperText={
                    payForm.tds && !CommonValidation.DecimalNumber(payForm.tds)
                      ? "Enter a valid amount"
                      : ""
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "" || CommonValidation.DecimalNumber(v)) {
                      setPayForm((p) => ({ ...p, tds: v }));
                    }
                  }}
                />
                <Box display="flex" justifyContent="space-between" sx={{ pt: 0.3 }}>
                  <Typography variant="caption" color="text.secondary">Deducted (Receivable + TDS)</Typography>
                  <Typography variant="caption" fontWeight={700} color="primary.main">
                    {CommonValidation.AmountFormat((+payForm.receivable || 0) + (+payForm.tds || 0))}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5 }}>Select Payment Mode</Typography>
                <RadioGroup row value={payForm.mode} onChange={(e) => setPayForm((p) => ({ ...p, mode: e.target.value }))}>
                  <FormControlLabel value="cash" control={<Radio size="small" />} label={<Typography fontSize="0.85vw">Cash</Typography>} />
                  <FormControlLabel value="card" control={<Radio size="small" />} label={<Typography fontSize="0.85vw">Card</Typography>} />
                  <FormControlLabel value="upi" control={<Radio size="small" />} label={<Typography fontSize="0.85vw">UPI</Typography>} />
                  <FormControlLabel value="bank" control={<Radio size="small" />} label={<Typography fontSize="0.85vw">Bank</Typography>} />
                  <FormControlLabel value="cheque" control={<Radio size="small" />} label={<Typography fontSize="0.85vw">Cheque</Typography>} />
                </RadioGroup>
                {payForm.mode === "card" && (
                  <TextField size="small" fullWidth label="Card Transaction No" value={payForm.card_no}
                    inputProps={{ maxLength: 50 }}
                    onChange={(e) => setPayForm((p) => ({ ...p, card_no: e.target.value }))} />
                )}
                {payForm.mode === "upi" && (
                  <TextField size="small" fullWidth label="UPI Transaction No" value={payForm.upi_no}
                    inputProps={{ maxLength: 50 }}
                    onChange={(e) => setPayForm((p) => ({ ...p, upi_no: e.target.value }))} />
                )}
                {payForm.mode === "bank" && (
                  <>
                    <TextField size="small" fullWidth label="Bank Reference No" value={payForm.bank_no}
                      inputProps={{ maxLength: 50 }}
                      onChange={(e) => setPayForm((p) => ({ ...p, bank_no: e.target.value }))} />
                    <Stack direction="row" spacing={1}>
                      <TextField size="small" fullWidth label="Payer Bank" value={payForm.payers_bank_name}
                        inputProps={{ maxLength: 50 }}
                        onChange={(e) => setPayForm((p) => ({ ...p, payers_bank_name: e.target.value }))} />
                      <TextField size="small" fullWidth label="Payer A/C No" value={payForm.payers_account_no}
                        inputProps={{ maxLength: 50 }}
                        onChange={(e) => setPayForm((p) => ({ ...p, payers_account_no: e.target.value }))} />
                    </Stack>
                  </>
                )}
                {payForm.mode === "cheque" && (
                  <Stack direction="row" spacing={1}>
                    <TextField size="small" fullWidth label="Cheque No" value={payForm.cheque_no}
                      inputProps={{ maxLength: 50 }}
                      onChange={(e) => setPayForm((p) => ({ ...p, cheque_no: e.target.value }))} />
                    <TextField size="small" fullWidth type="date" label="Cheque Date" InputLabelProps={{ shrink: true }}
                      value={payForm.cheque_date}
                      onChange={(e) => setPayForm((p) => ({ ...p, cheque_date: e.target.value }))} />
                  </Stack>
                )}
                <TextField size="small" fullWidth label="Transaction ID" value={payForm.transaction_id}
                  inputProps={{ maxLength: 50 }}
                  onChange={(e) => setPayForm((p) => ({ ...p, transaction_id: e.target.value }))} />
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500, mb: 0.3, display: "block" }}>
                    Comments *
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    placeholder="Enter comments"
                    value={payForm.comments}
                    inputProps={{ maxLength: 200 }}
                    onChange={(e) => setPayForm((p) => ({ ...p, comments: e.target.value }))}
                    sx={{
                      "& .MuiOutlinedInput-root": { padding: "8px 12px" },
                      "& .MuiInputBase-inputMultiline": { padding: 0, lineHeight: 1.5 },
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #e0e0e0", px: 2, py: 1 }}>
          <Button size="small" onClick={closePaymentPopup} disabled={paySaving}>Cancel</Button>
          <Button size="small" variant="contained" disableElevation onClick={handleSettleSubmit} disabled={paySaving}>
            {paySaving ? "Saving..." : "Settle Credit"}
          </Button>
        </DialogActions>
      </Dialog>

      {msg.err && <ToastMsg severity="error" msg={msg.errText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
      {msg.ok && <ToastMsg severity="success" msg={msg.okText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
    </Box>
  );
}
